import React, { useCallback } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { HorizontalBannerScroller } from '@/molecules/HorizontalBannerScroller'
import { useRecommendedHeroBanners } from '@/services/RecommendedBannerService'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { logger } from '@/utils/logging'
import { getObjectFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import useWindowSize from '@/utils/useWindowSize'
import { RecommendedBanner } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBanner/RecommendedBanner'
import { RecommendedBannerCarouselPlaceholder } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBannerCarouselPlaceholder'
import { RecommendedBannerCarouselFull } from './RecommendedBannerCarouselFull'
import { RecommendedBannerCarouselFullPlaceholder } from './RecommendedBannerCarouselFullPlaceholder'

const isNumericRegex = /^\d+$/

export const RecommendedBannerCarousel: React.FC = () => {
  const { innerWidth } = useWindowSize()
  const shouldShowUpdatedBanner = innerWidth >= 1024
  const { heroBanners, loading } = useRecommendedHeroBanners()
  const { trackRecommendation } = useRecommendationTracking()
  const trackImpressions = useCallback(
    (currentSlide: number) => {
      const el = document.querySelector(`.slick-slide[data-index="${currentSlide}"] .recommended-banner`)
      const recommendationId = el?.getAttribute('data-recommendation-id')

      if (!recommendationId) {
        logger().error('Attempting to track impression for recommendation banner that has no id!')
        return
      }

      if (isNumericRegex.test(recommendationId)) {
        return
      }

      const trackedImpressions = getObjectFromSessionStorage<Record<string, string>>('recommendationBannerImpressions')

      if (!trackedImpressions || !trackedImpressions[recommendationId]) {
        trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendationId! }] })

        writeToSessionStorage('recommendationBannerImpressions', {
          ...trackedImpressions,
          [recommendationId]: new Date(),
        })
      }
    },
    [trackRecommendation],
  )

  const trackFullImpressions = useCallback(
    (recommendationId: string) => {
      if (!recommendationId || isNumericRegex.test(recommendationId)) {
        return
      }

      const trackedImpressions = getObjectFromSessionStorage<Record<string, string>>('recommendationBannerImpressions')

      if (!trackedImpressions || !trackedImpressions[recommendationId]) {
        trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendationId! }] })

        writeToSessionStorage('recommendationBannerImpressions', {
          ...trackedImpressions,
          [recommendationId]: new Date(),
        })
      }
    },
    [trackRecommendation],
  )

  if (loading)
    return (
      <LazyHydrate whenVisible>
        <>
          <When condition={shouldShowUpdatedBanner}>
            <div data-testid="banners-placeholder">
              <RecommendedBannerCarouselFullPlaceholder />
            </div>
          </When>
          <When condition={!shouldShowUpdatedBanner}>
            <div data-testid="banners-placeholder">
              <RecommendedBannerCarouselPlaceholder />
            </div>
          </When>
        </>
      </LazyHydrate>
    )

  return (
    <LazyHydrate whenVisible>
      <>
        <When condition={shouldShowUpdatedBanner}>
          <RecommendedBannerCarouselFull heroBanners={heroBanners} trackImpressions={trackFullImpressions} />
        </When>
        <When condition={!shouldShowUpdatedBanner}>
          <HorizontalBannerScroller
            className="recommended-banner-carousel group-recommended-banner"
            centerMode
            centerPadding="32px"
            chevronClassName="group-recommended-banner-hover:block group-recommended-banner-hover:opacity-100"
            afterChange={trackImpressions}
          >
            {heroBanners?.map((banner) => {
              return <RecommendedBanner key={banner.id} banner={banner} />
            })}
          </HorizontalBannerScroller>
        </When>
      </>
    </LazyHydrate>
  )
}
